<template>
    <div
        ref="container"
        v-loading="loading"
        class="hts-map h-full">
        <div ref="mapTop">
            <slot name="top" />
        </div>
        <div
            ref="mapDiv"
            :style="{ 'min-height': height + 'px', 'max-height': height + 'px' }"
            class="hts-map-container" />
        <div>
            <slot
                ref="mapBottom"
                name="bottom" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Loader } from '@googlemaps/js-api-loader'
import { computed } from 'vue'
import CustomDirectionsRenderer from './custom-directions-renderer'
import Direction from './direction'
import Marker from './marker'

const props = defineProps({
  mapStyle: {
    type: String,
    default: ''
  },
  zoom: {
    type: Number,
    default: 15
  },
  travelMode: {
    type: String,
    default: 'DRIVING'
  },
  centerPosition: Object,
  routingData: {
    default: () => [],
    type: Array<Object>
  },
  polylineData: {
    default: () => [],
    type: Array<any>
  },
  minusHeight: {
    type: Number,
    default: 0
  },
  draggable: {
    type: Boolean
  }
})
const emits = defineEmits(['click', 'dataInformation', 'clickMarker', 'updateDirections'])

const container = ref<any>(null)
const mapTop = ref<any>(null)
const mapBottom = ref<any>(null)
const height = ref(200)
const loading = ref<boolean>(false)
const getHeight = () => {
  if (!container.value) {
    return
  }

  height.value = container.value?.clientHeight - (mapTop.value?.clientHeight || 0) - (mapBottom.value?.clientHeight || 0) - minusHeight.value
}

const { zoom, routingData, polylineData, travelMode, centerPosition, minusHeight, draggable } = toRefs(props)

const config = useRuntimeConfig().public
const MAP_KEY = config.mapKey
const loader = new Loader({
  apiKey: MAP_KEY,
  language: 'vi',
  version: 'beta'
})
/**
 * Map instance
 */
let map: null | google.maps.Map = null
const mapInstance = computed(() => map)
const isReady = ref(false)

/** Current browser GEO location */
const currentPosition = ref<GeolocationCoordinates | null>()

const mapDiv = ref<null | HTMLElement>(null)

const {
  MarkerLoader,
  markers,
  advancedMarkers,
  clickPositionMarker,
  createMarker,
  createAdvanceMarker,
  createAdvanceMarkers,
  removeMarkers,
  removeAdvanceMarkers,
  removeAdvanceMarker,
  removeAllMarkers,
  removeAllMarkerList,
  modifyAdvanceMarker,
  setVisibleAllMarkers,
  setAllMarkerSize,
  hideClickPositionMarker,
  showClickPositionMarker
} = Marker()

const {
  DirectionLoader,
  directionsRenderer,
  directionsRenderers,
  drawDirection,
  clearDirection,
  directionsService,
  drawDirectionMultiple,
  drawPolyline,
  clearRouting,
  resetMap,
  generateDirectionMarkers,
  clearPolyline,
  clearDirections,
  polylineRenderers,
  drawCustomDirection,
  clearCustomDirections
} = Direction()

/** On Mounted */
onMounted(async () => {
  loading.value = true
  getHeight()
  window.addEventListener('resize', getHeight)

  await initMap()

  if (centerPosition?.value) {
    setCenter(centerPosition.value as google.maps.LatLngAltitudeLiteral)
  }
  loading.value = false
  // drawDirectionMultiple(routingData.value, travelMode.value)
})

onBeforeUnmount(() => {
  window?.removeEventListener('resize', getHeight)
})

const initMap = async () => {
  if (!mapDiv.value) {
    return
  }

  await useGeolocation()

  await loader.importLibrary('places')
  await loader.importLibrary('maps')
  await loader.importLibrary('marker')
  await loader.importLibrary('routes')
  await loader.importLibrary('geocoding')
  await loader.importLibrary('geometry')

  directionsRenderer.value = new google.maps.DirectionsRenderer()
  directionsRenderers.value = []
  directionsService.value = new google.maps.DirectionsService()

  map = new google.maps.Map(mapDiv.value, {
    center: {
      lat: currentPosition.value?.latitude || 0,
      lng: currentPosition.value?.longitude || 0
    },
    zoom: zoom.value,
    disableDefaultUI: true,
    mapId: config.mapId,
    mapTypeId: 'roadmap',
    mapTypeControl: true, // Bật control để chọn kiểu bản đồ
    mapTypeControlOptions: {
      style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR, // Hoặc HORIZONTAL_BAR || DROPDOWN_MENU
      position: google.maps.ControlPosition.RIGHT_BOTTOM,
      mapTypeIds: ['roadmap', 'satellite']
    }
  })

  map.addListener('click', handleClick)
  setAllMarkerSize(zoom.value)
  directionsRenderer.value.setMap(map)

  MarkerLoader(map)
  DirectionLoader(map, emits)

  google.maps.event.addListener(map, 'zoom_changed', function () {
    if (!map) {
      return
    }
    const zoom = map.getZoom()
    if (zoom) {
      setVisibleAllMarkers(zoom > 5)
      setAllMarkerSize(zoom)
    }
  })

  isReady.value = true

  return map
}

const ready = async () => {
  let interval: NodeJS.Timeout | undefined
  return await new Promise((resolve) => {
    interval = setInterval(() => {
      if (isReady.value) {
        clearInterval(interval)
        resolve(true)
      }
    }, 100)
  })
}

const useGeolocation = async () => {
  try {
    const isSupported = 'navigator' in window && 'geolocation' in navigator

    if (isSupported && navigator.geolocation) {
      currentPosition.value = await new Promise<GeolocationCoordinates>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            resolve(position.coords)
          },
          (error: GeolocationPositionError) => {
            console.log('error', error)

            reject(error) // Thêm reject để xử lý lỗi
          },
          {
            enableHighAccuracy: false,
            timeout: 20000,
            maximumAge: 1000 * 60 * 60
          }
        )
      })
    }

    return {
      coords: currentPosition,
      isSupported
    }
  } catch (e: any) {
    console.log(e)
  }
}

const setCenter = (position: google.maps.LatLng | google.maps.LatLngLiteral) => {
  map?.setCenter(position)
}

/** Handle click on map */
const handleClick = (e: any) => {
  const {
    latLng: { lat, lng }
  }: { latLng: any } = e
  const position = {
    lat: lat(),
    lng: lng()
  }

  if (e.placeId) {
    clickPositionMarker.value && clickPositionMarker.value.setVisible(false)
    getPlaceDetail(e.placeId, position)
    return true
  }
  if (clickPositionMarker.value) {
    clickPositionMarker.value.setVisible(true)
    clickPositionMarker.value.setPosition(position)
  } else {
    clickPositionMarker.value = createMarker(position)
  }

  hideInfoWindow()
  emits('click', {
    place: e,
    ...position
  })
}

const hideInfoWindow = () => {
  const infoWindow = new google.maps.InfoWindow({
    content: ''
  })
  infoWindow.open({
    map
  })
  setTimeout(() => {
    infoWindow.close()
  }, 50)
}

const getPlaceDetail = (placeId: string, position: google.maps.LatLngLiteral) => {
  if (!map) {
    return
  }
  //* * Get place detail */
  const service = new google.maps.places.PlacesService(map)
  service.getDetails(
    {
      placeId
    },
    (place: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        if (place) {
          emits('click', position, place)
        }
      }
    }
  )
}

const fitBounds = (bounds: google.maps.LatLngBounds) => {
  map?.fitBounds(bounds)
}

watch(
  () => centerPosition?.value,
  (val: any) => {
    val && setCenter(val as google.maps.LatLng | google.maps.LatLngLiteral)
  },
  { deep: true }
)

watch(
  () => polylineData.value,
  () => {
    if (!polylineData.value.length) {
      return false
    }
    routingData.value.forEach((item: any, index: number) => {
      if (index !== 0 && index !== routingData.value.length - 1) {
        return true
      }
      const title = index === 0 ? $t('labels.map.origin') : $t('labels.map.destination')
      createMarker(
        {
          lat: item.lat,
          lng: item.lng
        },
        title
      )
    })
    ready().then(() => {
      drawPolyline(polylineData.value)
    })
  },
  { deep: true }
)

watch(
  () => draggable?.value,
  (val: boolean) => {
    if (val) {
      directionsRenderers.value.forEach((customRenderer: CustomDirectionsRenderer) => {
        customRenderer.renderer.setOptions({ draggable: true })

        customRenderer.renderer.addListener(
          'directions_changed',
          customRenderer.setDirectionsChangedListener(() => {
            emits('updateDirections', directionsRenderers.value)
          })
        )
      })
    } else {
      directionsRenderers.value.forEach((customRenderer: CustomDirectionsRenderer) => {
        customRenderer.renderer.setOptions({ draggable: false })
      })
    }
  }
)

const getMapInstance = () => {
  return map
}

const panTo = (location: { lat: number; lng: number }) => {
  if (!map) {
    return
  }

  map.setZoom(13)
  map.panTo(location)
}

defineExpose({
  ready,
  mapInstance,
  isReady,
  setCenter,
  markers,
  advancedMarkers,
  createMarker,
  createAdvanceMarker,
  createAdvanceMarkers,
  removeMarkers,
  removeAdvanceMarkers,
  removeAdvanceMarker,
  removeAllMarkers,
  removeAllMarkerList,
  modifyAdvanceMarker,
  setVisibleAllMarkers,
  hideInfoWindow,
  hideClickPositionMarker,
  showClickPositionMarker,
  drawDirection,
  resetMap,
  clearDirection,
  clearDirections,
  drawPolyline,
  clearPolyline,
  generateDirectionMarkers,
  fitBounds,
  getMapInstance,
  drawCustomDirection,
  clearCustomDirections,
  panTo
})
</script>
